import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { bucketFor, byEffortAscending, formatMinutes, EFFORT_META } from './choreEffort';
import './FocusMode.css';

const SPRINT_OPTIONS = [2, 5, 10, 20];

/**
 * One task, full screen, with a timer.
 *
 * Everything here exists to remove a decision. The board shows the whole house at once,
 * which is the right tool for planning and the wrong one for starting. Focus mode shows a
 * single task, picked for you, small enough to fit the time you said you had. "Skip" is
 * prominent on purpose: swapping the task is far cheaper than abandoning the session, and
 * an easy escape hatch keeps a bad match from ending the whole attempt.
 */
function FocusMode({ chores, rooms, initialRoomId, loading, onComplete, onSetEstimate }) {
  const { t, i18n } = useTranslation();

  const [roomFilter, setRoomFilter] = useState(initialRoomId ?? null);
  const [maxMinutes, setMaxMinutes] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [doneThisSession, setDoneThisSession] = useState([]);
  const [sprintLength, setSprintLength] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [running, setRunning] = useState(false);
  const celebrateRef = useRef(null);

  const roomName = useCallback(
    (room) => (i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn,
    [i18n.language]
  );

  useEffect(() => {
    if (initialRoomId !== undefined && initialRoomId !== null) setRoomFilter(initialRoomId);
  }, [initialRoomId]);

  const queue = useMemo(() => {
    return chores
      .filter(c => c.status !== 2)
      .filter(c => (roomFilter ? c.roomId === roomFilter : true))
      .filter(c => (maxMinutes ? c.estimatedMinutes != null && c.estimatedMinutes <= maxMinutes : true))
      .filter(c => !skipped.includes(c.id))
      .sort(byEffortAscending);
  }, [chores, roomFilter, maxMinutes, skipped]);

  const current = queue[0] || null;
  const minutesBanked = doneThisSession.reduce((sum, c) => sum + (c.estimatedMinutes || 0), 0);

  // ---- Timer ---------------------------------------------------------------
  useEffect(() => {
    if (!running || secondsLeft === null) return undefined;
    if (secondsLeft <= 0) {
      setRunning(false);
      return undefined;
    }
    const id = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [running, secondsLeft]);

  const startSprint = (minutes) => {
    setSprintLength(minutes);
    setSecondsLeft(minutes * 60);
    setRunning(true);
  };

  const stopSprint = () => {
    setRunning(false);
    setSprintLength(null);
    setSecondsLeft(null);
  };

  const timerExpired = secondsLeft !== null && secondsLeft <= 0;

  const formatClock = (total) => {
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // ---- Actions -------------------------------------------------------------
  const handleDone = async () => {
    if (!current) return;
    const finished = current;
    // Recorded locally as well as sent to the server: the session tally is the immediate
    // reward, and it shouldn't wait for a refetch to update.
    setDoneThisSession(prev => [...prev, finished]);
    if (celebrateRef.current) {
      celebrateRef.current.classList.remove('focus-celebrate-run');
      // Force reflow so the animation restarts on consecutive completions.
      void celebrateRef.current.offsetWidth;
      celebrateRef.current.classList.add('focus-celebrate-run');
    }
    await onComplete(finished.id);
  };

  const handleSkip = () => {
    if (current) setSkipped(prev => [...prev, current.id]);
  };

  const resetSkipped = () => setSkipped([]);

  const progressPct = sprintLength && secondsLeft !== null
    ? Math.max(0, (secondsLeft / (sprintLength * 60)) * 100)
    : 0;

  if (loading && chores.length === 0) {
    return <div className="chore-loading">{t('chores.focus.loading')}</div>;
  }

  return (
    <div className="focus-mode">
      {/* Filters */}
      <div className="focus-filters">
        <label className="focus-filter">
          <span className="focus-filter-label">{t('chores.focus.roomFilter')}</span>
          <select
            className="chore-select"
            value={roomFilter || ''}
            onChange={(e) => { setRoomFilter(e.target.value || null); resetSkipped(); }}
          >
            <option value="">{t('chores.focus.anywhere')}</option>
            {rooms.map(r => (
              <option key={r.id} value={r.id}>{r.icon} {roomName(r)}</option>
            ))}
          </select>
        </label>

        <label className="focus-filter">
          <span className="focus-filter-label">{t('chores.focus.timeAvailable')}</span>
          <select
            className="chore-select"
            value={maxMinutes || ''}
            onChange={(e) => { setMaxMinutes(e.target.value ? parseInt(e.target.value, 10) : null); resetSkipped(); }}
          >
            <option value="">{t('chores.focus.anyLength')}</option>
            <option value="2">{t('chores.focus.upTo', { minutes: 2 })}</option>
            <option value="5">{t('chores.focus.upTo', { minutes: 5 })}</option>
            <option value="15">{t('chores.focus.upTo', { minutes: 15 })}</option>
            <option value="30">{t('chores.focus.upTo', { minutes: 30 })}</option>
          </select>
        </label>
      </div>

      {/* Session tally */}
      <div className="focus-session" role="status" aria-live="polite">
        <span className="focus-session-item">
          ✅ {t('chores.focus.doneCount', { count: doneThisSession.length })}
        </span>
        {minutesBanked > 0 && (
          <span className="focus-session-item">
            ⏱ {t('chores.focus.minutesBanked', { time: formatMinutes(minutesBanked, t) })}
          </span>
        )}
        {skipped.length > 0 && (
          <button className="focus-session-reset" onClick={resetSkipped}>
            ↺ {t('chores.focus.unskip', { count: skipped.length })}
          </button>
        )}
      </div>

      {/* Sprint timer */}
      <div className={`focus-timer ${timerExpired ? 'focus-timer-expired' : ''}`}>
        {secondsLeft === null ? (
          <div className="focus-timer-start">
            <span className="focus-timer-prompt">{t('chores.focus.sprintPrompt')}</span>
            {SPRINT_OPTIONS.map(m => (
              <button key={m} className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => startSprint(m)}>
                {t('chores.focus.sprintMinutes', { count: m })}
              </button>
            ))}
          </div>
        ) : (
          <div className="focus-timer-running">
            <div className="focus-timer-bar">
              <div className="focus-timer-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="focus-timer-clock">{formatClock(Math.max(0, secondsLeft))}</span>
            {timerExpired ? (
              <span className="focus-timer-done">{t('chores.focus.sprintOver')}</span>
            ) : (
              <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => setRunning(r => !r)}>
                {running ? `⏸ ${t('chores.focus.pause')}` : `▶ ${t('chores.focus.resume')}`}
              </button>
            )}
            <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={stopSprint}>
              ✕ {t('chores.focus.stopSprint')}
            </button>
          </div>
        )}
      </div>

      {/* The one task */}
      {current ? (
        <div className="focus-card">
          <div ref={celebrateRef} className="focus-celebrate" aria-hidden="true">✨</div>

          <span className={`focus-card-bucket ${EFFORT_META[bucketFor(current.estimatedMinutes)].className}`}>
            {EFFORT_META[bucketFor(current.estimatedMinutes)].icon}{' '}
            {t(`chores.effort.${bucketFor(current.estimatedMinutes)}`)}
          </span>

          <h2 className="focus-card-title">{current.title}</h2>

          {current.description && <p className="focus-card-description">{current.description}</p>}

          <div className="focus-card-meta">
            {current.roomId && rooms.find(r => r.id === current.roomId) && (
              <span className="focus-card-chip">
                {rooms.find(r => r.id === current.roomId).icon}{' '}
                {roomName(rooms.find(r => r.id === current.roomId))}
              </span>
            )}
            {current.estimatedMinutes != null ? (
              <span className="focus-card-chip">⏱ {formatMinutes(current.estimatedMinutes, t)}</span>
            ) : (
              <span className="focus-card-estimate-prompt">
                {t('chores.focus.noEstimatePrompt')}
                {[2, 5, 15, 30].map(m => (
                  <button
                    key={m}
                    className="focus-estimate-chip"
                    onClick={() => onSetEstimate(current.id, m)}
                  >
                    {formatMinutes(m, t)}
                  </button>
                ))}
              </span>
            )}
          </div>

          <div className="focus-card-actions">
            <button className="focus-btn focus-btn-done" onClick={handleDone}>
              ✅ {t('chores.focus.markDone')}
            </button>
            <button className="focus-btn focus-btn-skip" onClick={handleSkip} disabled={queue.length < 2}>
              🔀 {t('chores.focus.somethingElse')}
            </button>
          </div>

          <p className="focus-remaining">
            {t('chores.focus.remaining', { count: queue.length - 1 })}
          </p>
        </div>
      ) : (
        <div className="focus-empty">
          <span className="focus-empty-icon" aria-hidden="true">
            {doneThisSession.length > 0 ? '🎉' : '🕊️'}
          </span>
          <h3>
            {doneThisSession.length > 0
              ? t('chores.focus.sessionComplete', { count: doneThisSession.length })
              : t('chores.focus.nothingMatches')}
          </h3>
          <p>
            {skipped.length > 0
              ? t('chores.focus.allSkipped')
              : t('chores.focus.adjustFilters')}
          </p>
          {skipped.length > 0 && (
            <button className="chore-btn chore-btn-primary chore-btn-small" onClick={resetSkipped}>
              ↺ {t('chores.focus.unskip', { count: skipped.length })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FocusMode;
